import type { HandRecord, PlayerIndex, Street } from "@joker-holdem/core";
import { CardView } from "./CardView.js";
import { categoryNameJa } from "./cardUtils.js";
import { chipsToBb, formatBb } from "../engine/selectors.js";

interface HandDetailModalProps {
  record: HandRecord;
  matchId: string;
  handIndex: number;
  timestamp: number;
  names: readonly [string, string];
  onClose: () => void;
}

const STREET_ORDER: readonly Street[] = ["preflop", "flop", "turn", "river"];
const STREET_LABEL_JA: Record<Street, string> = {
  preflop: "Preflop",
  flop: "Flop",
  turn: "Turn",
  river: "River",
};

const ACTION_LABEL: Record<string, string> = {
  fold: "Fold",
  check: "Check",
  call: "Call",
  bet: "Bet",
  raise: "Raise",
  allIn: "All-in",
};

function actionClass(type: string): string {
  if (type === "fold") return "hist-action-fold";
  if (type === "check" || type === "call") return "hist-action-call";
  return "hist-action-raise";
}

export function HandDetailModal({ record, matchId, handIndex, timestamp, names, onClose }: HandDetailModalProps) {
  const board = record.board;

  function boardForStreet(street: Street) {
    if (street === "preflop") return null;
    return (
      <div className="hist-board">
        <CardView joker size="sm" />
        {board.flopReal && (
          <>
            <CardView card={board.flopReal[0]} size="sm" />
            <CardView card={board.flopReal[1]} size="sm" />
          </>
        )}
        {street !== "flop" && board.turn !== undefined && <CardView card={board.turn} size="sm" />}
        {street === "river" && board.river !== undefined && <CardView card={board.river} size="sm" />}
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel hand-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{matchId} #{handIndex}</div>
            <div className="modal-subtitle">{new Date(timestamp).toLocaleString("ja-JP")}</div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="close">
            ×
          </button>
        </div>

        <div className="hist-participants">
          {([0, 1] as PlayerIndex[]).map((seat) => (
            <div key={seat} className="hist-participant">
              <span className="position-badge">{record.positions[seat]}</span>
              <span className="player-name">{names[seat]}</span>
              <div className="showdown-hole">
                <CardView card={record.holeCards[seat][0]} size="sm" />
                <CardView card={record.holeCards[seat][1]} size="sm" />
              </div>
              <span className={`hist-net ${record.netResult[seat] >= 0 ? "result-win" : "result-loss"}`}>
                {record.netResult[seat] >= 0 ? "+" : ""}
                {chipsToBb(record.netResult[seat]).toFixed(1)}bb
              </span>
            </div>
          ))}
        </div>

        {STREET_ORDER.filter((s) => record.streets[s]).map((street) => {
          const streetData = record.streets[street]!;
          return (
            <div key={street} className="hist-street-section">
              <div className="hist-street-header">
                <span className="hist-street-name">{STREET_LABEL_JA[street]}</span>
                <span className="hist-street-pot">Pot開始 {formatBb(streetData.potAtStart)}</span>
              </div>
              {boardForStreet(street)}
              <div className="hist-actions">
                {streetData.actions.length === 0 && <div className="hist-action-row">(アクションなし)</div>}
                {streetData.actions.map((a, i) => (
                  <div key={i} className={`hist-action-row ${actionClass(a.type)}`}>
                    <span className="position-badge">{record.positions[a.player]}</span>
                    <span>{names[a.player]}</span>
                    <span>{ACTION_LABEL[a.type] ?? a.type}</span>
                    {a.amount > 0 && <span>{formatBb(a.amount)}</span>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {record.result.endedBy === "showdown" && record.result.showdownDetails && (
          <div className="hist-street-section">
            <div className="hist-street-header">
              <span className="hist-street-name">SD</span>
            </div>
            {([0, 1] as PlayerIndex[]).map((seat) => {
              const detail = record.result.showdownDetails![seat];
              return (
                <div key={seat} className="hist-sd-row">
                  <span className="position-badge">{record.positions[seat]}</span>
                  <span>{names[seat]}</span>
                  <span>{categoryNameJa(detail.category)}</span>
                  <span>
                    ジョーカー→ <CardView card={detail.jokerCard} size="sm" />(見立て)
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <div className="hist-street-section hist-result-section">
          <div className="hist-street-header">
            <span className="hist-street-name">Result</span>
          </div>
          <div>
            {record.result.winner === "chop"
              ? "チョップ"
              : `${names[record.result.winner]} の勝ち: +${formatBb(record.result.potAwarded[record.result.winner])}`}
          </div>
        </div>
      </div>
    </div>
  );
}
